import ENV from "app/utils/ENV";
import {
  View,
  ScrollView,
  FlatList,
  ActivityIndicator,
  Dimensions,
} from "react-native";

const useFlatList = false;
const momentNoteWidth = ENV.WEB ? 300 : Dimensions.get("window").width * 0.8;

import React from "react";
import { Feather } from "@expo/vector-icons";
import Text from "app/components/Text";
import TouchableOpacity from "app/components/TouchableOpacity";
import DatePicker from "app/components/DatePicker";
import colorHelpers from "app/utils/colorHelpers";
// render-time-only use, so the MomentsLog↔MomentBullet cycle is safe (same
// pattern as Tag and RichText)
import MomentBullet from "app/components/chapter/MomentBullet";
import Badge from "app/components/chapter/Badge";
import valueActions from "app/utils/valueActions";
import chapterActions from "app/utils/chapterActions";
import timeHelpers from "app/utils/timeHelpers";
import textHelpers from "app/utils/textHelpers";
import valueHelpers from "app/utils/valueHelpers";
import * as RootNavigation from "app/utils/RootNavigation.js";

import MomentNote from "app/components/projects/MomentNote";

import store from "app/redux/store";
import { StyleContext } from "app/contexts/Style";

export default class MomentsLog extends React.Component {
  static contextType = StyleContext;
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      ...this.deriveStateFromProps(props),
    };
  }

  deriveStateFromProps = (props) => {
    // copy before mutating — props.moments is the parent's state array
    let moments = [...props.moments];
    if (useFlatList) moments = moments.reverse();
    const momentIndex = moments.findIndex((m) => m.id == props.moment?.id);
    if (!!props?.value.completed_at)
      moments.push({
        completed_at: props.value.completed_at,
        type: "completion",
      });
    if (!!props?.value.archived_at)
      moments.push({
        completed_at: props.value.archived_at,
        type: "archive",
      });
    // completed children appear as narrow nodes in the row
    let children =
      valueHelpers.getChildren({ value: props.value, includeNotes: true }) ||
      [];
    children
      .filter((c) => !!c.completed_at)
      .forEach((c) =>
        moments.push({
          id: `child-completion-${c.id}`,
          type: "child_completion",
          completed_at: c.completed_at,
          value: c,
        }),
      );
    moments = textHelpers.oldestFirst(
      moments,
      "chapter_start_at",
      "completed_at",
    );
    return {
      pinnedIds: props.value?.pinned_moment_ids,
      moments,
      momentIndex,
      color: valueHelpers.getColor(props.value),
    };
  };

  componentDidMount = () => {};

  componentDidUpdate = (prevProps) => {
    // re-derive from props so refreshed moments (completions, edited
    // text, late fetches) render without remounting the log
    if (
      prevProps.moments !== this.props.moments ||
      prevProps.value?.completed_at !== this.props.value?.completed_at ||
      prevProps.value?.pinned_moment_ids !== this.props.value?.pinned_moment_ids
    ) {
      this.setState(this.deriveStateFromProps(this.props));
    }
  };

  // "+" pins a new moment to TODAY regardless of which chapter is being
  // viewed; if today already has one, open a date picker instead (like the
  // calendar action on a bullet)
  handleAddMoment = async () => {
    let { value, moments, chapters } = this.props;
    let todayStr = timeHelpers.dateTimeToDate(new Date());
    let todayChapter = (chapters || []).find(
      (c) => c.range == 1 && c.start_at == todayStr,
    );
    let alreadyToday =
      !!todayChapter &&
      (moments || []).some((m) => m.chapter_id == todayChapter.id);
    if (alreadyToday) {
      this.setState({ showAddDatePicker: true, addDate: new Date() });
      return;
    }
    await chapterActions.pinValue("today", value.id);
    this.props.onRefreshMoment && this.props.onRefreshMoment();
  };

  handleAddForDate = async (date) => {
    let { value } = this.props;
    let dateStr = timeHelpers.dateTimeToDate(date);
    let res = await chapterActions.pinValueByDate(dateStr, value);
    this.setState({ showAddDatePicker: false });
    if (!res?.success) {
      alert("Could not pin a moment to that date");
      return;
    }
    this.props.onRefreshMoment && this.props.onRefreshMoment();
  };

  // ---- generation drill-down: each press of "deeper" adds the next
  // generation's moments and the generation below's completions ----

  // arrays of value objects per generation (1 = children, 2 = grandchildren…)
  generationValues = (depth) => {
    let values = store.getState().values;
    let subValues = this.state.subMomentValues || [];
    let byId = (id) =>
      values.find((v) => v.id == id) || subValues.find((v) => v.id == id);
    let gens = [];
    let current = [this.props.value];
    for (let g = 1; g <= depth; g++) {
      let next = [];
      let seen = new Set();
      current.forEach((v) =>
        (v?.child_ids || []).forEach((cid) => {
          if (seen.has(cid)) return;
          seen.add(cid);
          let cv = byId(cid);
          if (cv) next.push(cv);
        }),
      );
      gens.push(next);
      if (!next.length) break;
      current = next;
    }
    return gens;
  };

  hasDeeperGeneration = () => {
    let depth = this.state.generationDepth || 0;
    let gens = this.generationValues(depth + 1);
    return !!gens[depth]?.length;
  };

  loadNextGeneration = async () => {
    if (this.state.loadingGeneration) return;
    let { value } = this.props;
    if (!this.state.subMoments) {
      // one fetch brings ALL descendant moments (+ their lite values), so
      // going deeper after this is purely client-side
      this.setState({ loadingGeneration: true });
      let res = await valueActions.get(value.id, null, true);
      this.setState({
        loadingGeneration: false,
        subMoments: res?.sub_moments || [],
        subMomentValues: res?.sub_moment_values || [],
        subMomentChapters: res?.sub_moment_chapters || [],
      });
    }
    this.setState({
      generationDepth: (this.state.generationDepth || 0) + 1,
    });
  };

  // merge deeper generations into the base row items, sorted chronologically
  withGenerations = (baseMoments) => {
    let { generationDepth, subMoments } = this.state;
    if (!generationDepth || !subMoments) return baseMoments;
    let gens = this.generationValues(generationDepth + 1);
    let items = [...baseMoments];
    let seenMoments = new Set(items.map((m) => m.id));
    for (let g = 1; g <= generationDepth; g++) {
      (gens[g - 1] || []).forEach((v) => {
        subMoments
          .filter((m) => m.value_id == v.id)
          .forEach((m) => {
            if (seenMoments.has(m.id)) return;
            seenMoments.add(m.id);
            items.push({ ...m, value: v, generation: g });
          });
      });
    }
    // completions one generation below the deepest loaded moments
    let seenDone = new Set(
      items.filter((i) => i.type == "child_completion").map((i) => i.id),
    );
    for (let g = 2; g <= generationDepth + 1; g++) {
      (gens[g - 1] || []).forEach((v) => {
        if (!v.completed_at) return;
        let id = `child-completion-${v.id}`;
        if (seenDone.has(id)) return;
        seenDone.add(id);
        items.push({
          id,
          type: "child_completion",
          completed_at: v.completed_at,
          value: v,
        });
      });
    }
    return textHelpers.oldestFirst(items, "chapter_start_at", "completed_at");
  };

  // short date for completed-child nodes: "Tues 9/30/26" — the app's usual
  // weekday abbreviation, with the year always included
  formatShortDate = (d) => {
    let date = new Date(d);
    return `${timeHelpers.getDayOfWeek(date, true)} ${
      date.getMonth() + 1
    }/${date.getDate()}/${String(date.getFullYear()).slice(2)}`;
  };

  // a completed child renders as a narrow vertical node; clicking it swaps
  // in a full expanded MomentBullet (escape/collapse returns to the node)
  renderChildCompletion = (item, zIndex) => {
    let child = item.value;
    // same color derivation as the moments in this row (no scheme arg —
    // passing one selects a different palette map than the moments use)
    let childColor = valueHelpers.getColor(child) || this.context.colors.trim;
    let expanded = this.state.expandedChildCompletionId == item.id;
    if (expanded) {
      return (
        <View
          key={item.id}
          style={{
            zIndex,
            width: 320,
            borderWidth: 1,
            borderColor: colorHelpers.hexToRgbaString(childColor, 0.6),
            borderRadius: this.context.sizes.borderRadius,
            // keep the collapsed node's pale value-color fill
            backgroundColor: colorHelpers.hexToRgbaString(childColor, 0.2),
            padding: this.context.sizes.small,
            marginRight: this.context.sizes.small,
          }}
        >
          <MomentBullet
            moment={{ value: child }}
            expanded
            onEscape={() =>
              this.setState({ expandedChildCompletionId: null })
            }
          />
          {/* mouse equivalent of escape */}
          <TouchableOpacity
            style={{
              position: "absolute",
              bottom: this.context.sizes.tiny,
              right: this.context.sizes.tiny,
              padding: this.context.sizes.tiny,
              zIndex: 9,
            }}
            onPress={() =>
              this.setState({ expandedChildCompletionId: null })
            }
          >
            <Feather
              name="x"
              style={{
                fontSize: this.context.sizes.default,
                color: this.context.colors.trim,
              }}
            />
          </TouchableOpacity>
        </View>
      );
    }
    return (
      <TouchableOpacity
        key={item.id}
        onPress={() =>
          this.setState({ expandedChildCompletionId: item.id })
        }
      >
        <View
          style={{
            zIndex,
            width: 144,
            // mirror a completed MomentNote card: same radius/padding,
            // borderless with the pale value-color fill, natural height
            borderRadius: this.context.sizes.borderRadius,
            backgroundColor: colorHelpers.hexToRgbaString(childColor, 0.2),
            padding: this.context.sizes.small,
            marginRight: this.context.sizes.small,
          }}
        >
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Text medium style={{ color: childColor, flex: 1 }}>
              {this.formatShortDate(item.completed_at)}
            </Text>
            <Badge
              moment={{ value: child, completed_at: item.completed_at }}
              backgroundColor={childColor}
              iconColor={this.context.colors.lightForeground}
              noTooltip
            />
          </View>
          <Text
            numberOfLines={4}
            strike
            style={{ color: childColor }}
          >
            {child.title || child.text}
          </Text>
        </View>
      </TouchableOpacity>
    );
  };

  focus = () => {
    let { chapter } = this.props;
    let { moments } = this.state;
    let moment = moments[moments.length - 1]; // Default to last moment
    // Try to find moment associated with rootChapter first
    if (chapter) {
      let chapterMoment = moments.find((m) => m.chapter_id == chapter.id);
      if (chapterMoment) moment = chapterMoment;
    }
    let ref = this[`moment-note-ref-${moment?.id}`];
    ref?.focus();
  };

  focusPreviousMoment = (currentMomentId) => {
    let { moments } = this.state;
    let currentIndex = moments.findIndex((m) => m.id == currentMomentId);
    if (currentIndex > 0) {
      let prevMoment = moments[currentIndex - 1];
      let ref = this[`moment-note-ref-${prevMoment.id}`];
      ref?.focus();
    }
  };

  focusNextMoment = (currentMomentId) => {
    let { moments } = this.state;
    let currentIndex = moments.findIndex((m) => m.id == currentMomentId);
    if (currentIndex >= 0 && currentIndex < moments.length - 1) {
      let nextMoment = moments[currentIndex + 1];
      let ref = this[`moment-note-ref-${nextMoment.id}`];
      ref?.focus();
    }
  };

  render() {
    // let { chapters, moment } = this.props;
    // let { moments, momentIndex, color } = this.state;
    let { chapters, moment } = this.props;
    let { momentIndex, color, pinnedIds } = this.state;
    // include any loaded deeper generations, sorted chronologically
    let moments = this.withGenerations(this.state.moments);

    return (
      <View style={[this.styles().wrapper]}>
        {!!pinnedIds?.length && (
          <ScrollView
            nestedScrollEnabled
            horizontal
            style={{
              padding: this.context.sizes.base,
            }}
          >
            {moments
              .filter((x) => pinnedIds.includes(x.id))
              .map((item, index) => {
                let chapter = chapters?.find((ch) => ch.id == item?.chapter_id);
                return (
                  <View
                    key={item.id}
                    // descending zIndex so a card's tooltips render above
                    // the cards that follow it in the row
                    style={{ zIndex: moments.length - index }}
                  >
                  <MomentNote
                    ref={(r) => (this[`moment-note-ref-${item.id}`] = r)}
                    isPinned
                    moment={item}
                    value={this.props.value}
                    chapter={chapter}
                    color={color}
                    expanded={item.id == moment?.id}
                    focused={item.id == moment?.id}
                    rootMoment={moment}
                    width={momentNoteWidth}
                    onFocus={() => {
                      return;
                      let momentPos = 0;
                      moments.forEach((m, i) => {
                        if (i < index) {
                          let isPin = !!chapter && chapter.range > 1;
                          if (isPin) momentPos += 42;
                          else momentPos += momentNoteWidth;
                          momentPos += this.context.sizes.small;
                        }
                      });
                      this.scrollerRef.scrollTo({
                        x: momentPos,
                        animated: true,
                      });
                    }}
                    onRefreshChapter={this.props.onRefreshChapter}
                    onRefreshMoment={this.props.onRefreshMoment}
                    focusPrevious={this.props.focusPrevious}
                    focusNext={this.props.focusNext}
                    focusPreviousMoment={() => this.focusPreviousMoment(item.id)}
                    focusNextMoment={() => this.focusNextMoment(item.id)}
                  />
                  </View>
                );
              })}
          </ScrollView>
        )}
        {!useFlatList ? (
          <ScrollView
            nestedScrollEnabled
            horizontal
            style={{
              padding: this.context.sizes.base,
            }}
            contentContainerStyle={{}}
            ref={(ref) => (this.scrollerRef = ref)}
            onLayout={() => {
              if (!this.initialScrollComplete) {
                if (!!moment?.id) {
                  let momentPos =
                    momentIndex * (momentNoteWidth + this.context.sizes.small);
                  this.scrollerRef.scrollTo({ x: momentPos, animated: false });
                } else {
                  this.scrollerRef?.scrollToEnd({ animated: false });
                }
                this.initialScrollComplete = true;
              }
            }}
          >
            {moments.map((item, index) => {
              if (item.type == "child_completion") {
                return this.renderChildCompletion(
                  item,
                  moments.length - index,
                );
              }
              let chapter =
                chapters?.find((ch) => ch.id == item?.chapter_id) ||
                this.state.subMomentChapters?.find(
                  (ch) => ch.id == item?.chapter_id,
                ) ||
                store.getState().chapters?.find((ch) => ch.id == item?.chapter_id) ||
                // last resort: synthesize a day chapter from the moment's
                // recorded chapter date so it never reads "No chapter?"
                (!!item?.chapter_start_at
                  ? {
                      id: item.chapter_id,
                      start_at: item.chapter_start_at,
                      end_at: item.chapter_start_at,
                      range: 1,
                    }
                  : null);
              // deeper-generation moments belong to their own value
              let itemValue = item.value || this.props.value;
              let itemColor = item.value
                ? valueHelpers.getColor(item.value) || color
                : color;
              return (
                <View
                  key={item.id}
                  // descending zIndex so a card's tooltips render above
                  // the cards that follow it in the row
                  style={{ zIndex: moments.length - index }}
                >
                <MomentNote
                  ref={(r) => (this[`moment-note-ref-${item.id}`] = r)}
                  isPinned={pinnedIds.includes(item.id)}
                  moment={item}
                  value={itemValue}
                  onBrowseEscape={this.props.onBrowseEscape}
                  // deeper-generation moments label themselves with their
                  // value's title in the value's color
                  showValueTitle={
                    !!item.value && item.value.id != this.props.value?.id
                  }
                  chapter={chapter}
                  color={itemColor}
                  expanded={item.id == moment?.id}
                  focused={item.id == moment?.id}
                  rootMoment={moment}
                  width={momentNoteWidth}
                  onFocus={() => {
                    return;
                    let momentPos = 0;
                    moments.forEach((m, i) => {
                      if (i < index) {
                        let isPin = !!chapter && chapter.range > 1;
                        if (isPin) momentPos += 42;
                        else momentPos += momentNoteWidth;
                        momentPos += this.context.sizes.small;
                      }
                    });
                    this.scrollerRef.scrollTo({ x: momentPos, animated: true });
                  }}
                  onRefreshChapter={this.props.onRefreshChapter}
                  onRefreshMoment={this.props.onRefreshMoment}
                  focusPrevious={this.props.focusPrevious}
                  focusNext={this.props.focusNext}
                  focusPreviousMoment={() => this.focusPreviousMoment(item.id)}
                  focusNextMoment={() => this.focusNextMoment(item.id)}
                />
                </View>
              );
            })}
            {!!this.props.value?.id &&
              (this.state.showAddDatePicker ? (
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                  <DatePicker
                    dateOnly
                    selected={this.state.addDate || new Date()}
                    value={this.state.addDate || new Date()}
                    // picking only stages the date — ✓ or Enter commits it
                    onChange={(date) => this.setState({ addDate: date })}
                    onKeyDown={(e) => {
                      if (e?.key == "Enter") {
                        // let the picker's own Enter-selection update state
                        // first, then commit
                        setTimeout(
                          () =>
                            this.handleAddForDate(
                              this.state.addDate || new Date(),
                            ),
                          50,
                        );
                      }
                    }}
                  />
                  <Feather
                    name="check"
                    style={{
                      fontSize: this.context.sizes.large,
                      color: this.context.colors.active,
                      paddingHorizontal: this.context.sizes.small,
                    }}
                    onPress={() =>
                      this.handleAddForDate(this.state.addDate || new Date())
                    }
                  />
                  <Feather
                    name="x"
                    style={{
                      fontSize: this.context.sizes.large,
                      color: this.context.colors.trim,
                    }}
                    onPress={() => this.setState({ showAddDatePicker: false })}
                  />
                </View>
              ) : (
                <View style={{ alignSelf: "flex-start", alignItems: "center" }}>
                  <Text
                    button
                    style={{ minWidth: 60, textAlign: "center" }}
                    onPress={this.handleAddMoment}
                  >
                    +
                  </Text>
                  {this.hasDeeperGeneration() && (
                    <Text
                      smallButton
                      style={{ marginTop: this.context.sizes.tiny }}
                      onPress={this.loadNextGeneration}
                    >
                      {this.state.loadingGeneration ? "…" : "⌄ deeper"}
                    </Text>
                  )}
                </View>
              ))}
          </ScrollView>
        ) : (
          <FlatList
            horizontal
            style={{
              padding: this.context.sizes.base,
              borderWidth: 1,
              width: "100%",
            }}
            contentContainerStyle={{
              width: "100%",
            }}
            inverted
            // data={moments.reverse()}
            data={moments}
            keyExtractor={(item) => `moment=${item.id}`}
            renderItem={({ item, index }) => (
              <MomentNote
                ref={(r) => (this[`moment-note-ref-${item.id}`] = r)}
                isPinned={pinnedIds.includes(item.id)}
                moment={item}
                chapter={chapters?.find((ch) => ch.id == item?.chapter_id)}
                color={color}
                expanded={item.id == moment?.id}
                focused={item.id == moment?.id}
                width={momentNoteWidth}
                onRefreshChapter={this.props.onRefreshChapter}
                onRefreshMoment={this.props.onRefreshMoment}
                focusPrevious={this.props.focusPrevious}
                focusNext={this.props.focusNext}
              />
            )}
          />
        )}
      </View>
    );
  }

  styles = () => ({
    wrapper: {
      flex: 1,
      // marginTop: this.context.sizes.giant,
      marginHorizontal: this.context.sizes.base,
      alignSelf: "stretch",
    },
  });
}
